import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useBreadcrumbContext, type NavbarSlotName } from '../contexts/breadcrumb-context';

interface NavbarPortalProps {
  slot: NavbarSlotName;
  children: ReactNode;
  /**
   * What to draw where the portal stands when there is no navbar to draw into:
   * the children themselves (the default), or nothing at all — for something
   * that only makes sense as part of the navbar, like a strip on its border.
   */
  fallback?: 'inline' | 'none';
}

/**
 * Draws its children into one of the navbar's slots (see NavbarSlots), and
 * keeps them the page's: state, effects and contexts are where the portal
 * stands, only the pixels are in the navbar.
 *
 * Where there is no navbar — the gym-admin app, a page under test — the
 * children stay where they are, so a page's controls never depend on the shell.
 */
export function NavbarPortal({ slot, children, fallback = 'inline' }: NavbarPortalProps) {
  const { navbarSlots } = useBreadcrumbContext();
  const target = navbarSlots?.[slot] ?? null;

  if (target) return createPortal(children, target);
  return fallback === 'inline' ? <>{children}</> : null;
}
